import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Users, Wallet, Building2, Inbox, LogOut, ShieldAlert, Check, X as XIcon, Clock, Trash2, ExternalLink, Search } from "lucide-react";

function fmtPrice(price, currency) {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency || "EUR", maximumFractionDigits: 0, currencyDisplay: "narrowSymbol" }).format(price);
  } catch {
    return `${currency || "EUR"} ${Number(price).toLocaleString()}`;
  }
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="hairline bg-white p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div className="font-display text-4xl font-black tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("agents");
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [listings, setListings] = useState([]);
  const [query, setQuery] = useState("");
  const [confirmDel, setConfirmDel] = useState(null); // {property_id, title}
  const [delReason, setDelReason] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const meRes = await api.get("/auth/me");
      if (meRes.data.role !== "admin") {
        navigate("/admin/login");
        return;
      }
      setMe(meRes.data);
      const [s, a, p, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/agents"),
        api.get("/admin/payments"),
        api.get("/admin/properties"),
      ]);
      setStats(s.data); setAgents(a.data); setPayments(p.data); setListings(l.data);
    } catch {
      navigate("/admin/login");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("token"); localStorage.removeItem("user");
    navigate("/admin/login");
  };

  const verifyPayment = async (id, status) => {
    try {
      await api.post(`/admin/payments/${id}/verify`, { status });
      setPayments((prev) => prev.map((p) => p.claim_id === id ? { ...p, status } : p));
      toast.success(status === "verified" ? "Payment verified — subscription extended 30 days" : "Payment marked " + status);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/properties/${confirmDel.property_id}`, { data: { reason: delReason } });
      setListings((prev) => prev.filter((x) => x.property_id !== confirmDel.property_id));
      toast.success("Listing removed");
      setConfirmDel(null); setDelReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const filteredListings = listings.filter((p) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [p.title, p.city, p.country, p.agent_name, p.description].filter(Boolean).some((s) => s.toLowerCase().includes(q));
  });

  if (!me) {
    return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <aside className="fixed inset-y-0 left-0 w-64 hidden md:flex flex-col hairline border-l-0 border-t-0 border-b-0 p-6 bg-white">
        <Link to="/" className="font-display text-lg font-medium tracking-tight flex items-center gap-2 mb-2">
          <span className="w-6 h-6 grid place-items-center bg-black text-white font-black">Æ</span>
          Aegis Realtor
        </Link>
        <div className="flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase text-muted-foreground mb-10">
          <ShieldAlert className="w-3 h-3" /> Admin
        </div>
        <nav className="flex-1 flex flex-col gap-1 text-sm">
          <button data-testid="admin-tab-agents" onClick={() => setTab("agents")} className={`flex items-center gap-3 px-3 py-2 rounded-md text-left ${tab === "agents" ? "bg-black text-white" : "hover:bg-[var(--paper-2)]"}`}>
            <Users className="w-4 h-4" strokeWidth={1.75} /> Agents
          </button>
          <button data-testid="admin-tab-listings" onClick={() => setTab("listings")} className={`flex items-center gap-3 px-3 py-2 rounded-md text-left ${tab === "listings" ? "bg-black text-white" : "hover:bg-[var(--paper-2)]"}`}>
            <Building2 className="w-4 h-4" strokeWidth={1.75} /> Listings
          </button>
          <button data-testid="admin-tab-payments" onClick={() => setTab("payments")} className={`flex items-center gap-3 px-3 py-2 rounded-md text-left justify-between ${tab === "payments" ? "bg-black text-white" : "hover:bg-[var(--paper-2)]"}`}>
            <span className="flex items-center gap-3"><Wallet className="w-4 h-4" strokeWidth={1.75} /> Payments</span>
            {stats?.pending_payments > 0 && (
              <span data-testid="admin-pending-badge" className={`text-[10px] font-bold px-1.5 rounded-full ${tab === "payments" ? "bg-white text-black" : "bg-black text-white"}`}>
                {stats.pending_payments}
              </span>
            )}
          </button>
        </nav>
        <div className="text-xs text-muted-foreground">
          <div className="mb-2 truncate">{me.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 hover:text-black">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="md:pl-64 p-6 sm:p-10">
        <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground mb-3">Command center</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Overview</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatCard label="Agents" value={stats?.agents} icon={Users} />
          <StatCard label="Listings" value={stats?.properties} icon={Building2} />
          <StatCard label="Buyer leads" value={stats?.leads} icon={Inbox} />
          <StatCard label="Pending payments" value={stats?.pending_payments} icon={Wallet} />
        </div>

        <div className="mt-12">
          {tab === "agents" && (
            <div>
              <h2 className="font-display text-2xl font-medium mb-4">All agents</h2>
              {agents.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">No agents registered yet.</div>
              ) : (
                <div className="hairline bg-white overflow-x-auto">
                  <table className="w-full text-sm" data-testid="agents-table">
                    <thead>
                      <tr className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-[var(--line)]">
                        <th className="text-left px-4 py-3 font-bold">Agent</th>
                        <th className="text-left px-4 py-3 font-bold">Agency</th>
                        <th className="text-left px-4 py-3 font-bold">Contact</th>
                        <th className="text-left px-4 py-3 font-bold">Status</th>
                        <th className="text-right px-4 py-3 font-bold">Listings</th>
                        <th className="text-right px-4 py-3 font-bold">Leads</th>
                        <th className="text-right px-4 py-3 font-bold">Payments</th>
                        <th className="text-left px-4 py-3 font-bold">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((a) => (
                        <tr key={a.user_id} data-testid={`agent-row-${a.user_id}`} className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--paper-2)]">
                          <td className="px-4 py-3">
                            <div className="font-medium">{a.name || "—"}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">{a.email}</div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{a.agency || "—"}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{a.phone || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 ${a.subscription_status === "active" ? "bg-black text-white" : "hairline"}`}>
                              {a.subscription_status || "trial"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono tabular-nums">{a.property_count}</td>
                          <td className="px-4 py-3 text-right font-mono tabular-nums">{a.lead_count}</td>
                          <td className="px-4 py-3 text-right font-mono tabular-nums">{a.payment_count}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "listings" && (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="font-display text-2xl font-medium">Listings moderation</h2>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    data-testid="listings-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by title, city, agent..."
                    className="pl-9 pr-3 py-2 hairline bg-white focus:border-black outline-none text-sm w-72"
                  />
                </div>
              </div>
              {filteredListings.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  {query ? "No listings match your search." : "No listings yet."}
                </div>
              ) : (
                <div className="flex flex-col gap-3" data-testid="admin-listings">
                  {filteredListings.map((p) => (
                    <div key={p.property_id} data-testid={`admin-listing-${p.property_id}`} className="hairline bg-white p-4 flex flex-col md:flex-row gap-4 md:items-center">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.title} className="w-full md:w-28 h-28 object-cover" />
                      ) : (
                        <div className="w-full md:w-28 h-28 bg-[var(--paper-2)] grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">no image</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display text-lg font-medium truncate">{p.title}</h3>
                          {p.agent_id === "seed" && <span className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 hairline">seed</span>}
                          {p.source_url && <span className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 hairline">imported</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{p.city}, {p.country} · {p.property_type}</div>
                        <div className="mt-1 font-mono text-sm">{fmtPrice(p.price, p.currency)}</div>
                        <div className="mt-2 text-xs text-muted-foreground truncate">
                          Agent: <span className="text-black">{p.agent_name || "—"}</span>
                          {p.created_at && <> · <span className="font-mono">{new Date(p.created_at).toLocaleDateString()}</span></>}
                        </div>
                        {p.description && <p className="mt-2 text-xs text-muted-foreground line-clamp-2 max-w-2xl">{p.description}</p>}
                      </div>
                      <div className="flex md:flex-col gap-2">
                        <a
                          data-testid={`admin-view-${p.property_id}`}
                          href={`/property/${p.property_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 hairline text-xs uppercase tracking-[0.2em] font-bold hover:bg-[var(--paper-2)]"
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                        <button
                          data-testid={`admin-delete-${p.property_id}`}
                          onClick={() => { setConfirmDel(p); setDelReason(""); }}
                          className="flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs uppercase tracking-[0.2em] font-bold hover:opacity-90"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "payments" && (
            <div>
              <h2 className="font-display text-2xl font-medium mb-4">Payment notifications</h2>
              {payments.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">No payment claims yet.</div>
              ) : (
                <div className="flex flex-col gap-3" data-testid="payments-list">
                  {payments.map((p) => (
                    <motion.div
                      key={p.claim_id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid={`payment-${p.claim_id}`}
                      className="hairline bg-white p-5 flex flex-col md:flex-row gap-4 md:items-center"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="font-display text-lg font-medium">{p.agent_name || p.agent_email}</div>
                          <span data-testid={`payment-status-${p.claim_id}`} className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 ${p.status === "verified" ? "bg-black text-white" : p.status === "rejected" ? "hairline text-muted-foreground" : "bg-[var(--btc)] text-black"}`}>
                            {p.status}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{p.agent_email}</div>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <span className="font-mono">${p.amount_usd} · {p.currency}</span>
                          {p.tx_hash && <span className="font-mono text-xs text-muted-foreground truncate max-w-[240px]">tx: {p.tx_hash}</span>}
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}</span>
                        </div>
                        {p.note && <p className="mt-2 text-sm italic text-muted-foreground">&ldquo;{p.note}&rdquo;</p>}
                      </div>
                      {p.status === "pending" && (
                        <div className="flex gap-2">
                          <button
                            data-testid={`verify-${p.claim_id}`}
                            onClick={() => verifyPayment(p.claim_id, "verified")}
                            className="flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs uppercase tracking-[0.2em] font-bold"
                          >
                            <Check className="w-3 h-3" /> Verify
                          </button>
                          <button
                            data-testid={`reject-${p.claim_id}`}
                            onClick={() => verifyPayment(p.claim_id, "rejected")}
                            className="flex items-center gap-1.5 px-3 py-2 hairline text-xs uppercase tracking-[0.2em] font-bold hover:bg-[var(--paper-2)]"
                          >
                            <XIcon className="w-3 h-3" /> Reject
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {confirmDel && (
        <div className="fixed inset-0 z-50 grid place-items-center px-6 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDel(null)}>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white hairline p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">Confirm removal</div>
            <div className="font-display text-2xl font-medium mb-3">Delete this listing?</div>
            <p className="text-sm text-muted-foreground mb-1 truncate"><span className="text-black">{confirmDel.title}</span></p>
            <p className="text-xs text-muted-foreground mb-6">{confirmDel.city}, {confirmDel.country} · Agent: {confirmDel.agent_name || "—"}</p>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Reason (optional)</label>
            <textarea
              data-testid="delete-reason"
              rows={2}
              value={delReason}
              onChange={(e) => setDelReason(e.target.value)}
              placeholder="e.g. inappropriate content, spam, misleading price"
              className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none text-sm"
            />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirmDel(null)} className="px-5 py-2 hairline text-sm">Cancel</button>
              <button data-testid="delete-confirm" onClick={doDelete} className="px-5 py-2 bg-black text-white text-sm">Delete listing</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
